import { flattenConnection } from "@shopify/hydrogen-react";
import type {
  Collection,
  Image,
  Product,
} from "@shopify/hydrogen-react/storefront-api-types";
import { useQuery } from "react-query";
import { client } from "./client";

const COLLECTION_QUERY = `#graphql
query collection($handle: String!, $numProducts: Int = 30) {
  collection(handle: $handle) {
    id
    title
    description
    descriptionHtml
    handle
    image {
      id
      altText
      height
      url
      width
    }
    products(first: $numProducts) {
      nodes {
        id
        artistId: metafield(namespace: "hug", key: "artist_id") {
          value
        }
        availableForSale
        handle
        productType
        title
        featuredImage {
          id
          altText
          height
          url
          width
        }
        compareAtPriceRange {
          minVariantPrice {
            amount
            currencyCode
          }
          maxVariantPrice {
            amount
            currencyCode
          }
        }
        priceRange {
          minVariantPrice {
            amount
            currencyCode
          }
          maxVariantPrice {
            amount
            currencyCode
          }
        }
      }
    }
  }
}`;

type ProductNode = Pick<
  Product,
  | "id"
  | "availableForSale"
  | "featuredImage"
  | "handle"
  | "productType"
  | "title"
  | "compareAtPriceRange"
  | "priceRange"
> & {
  artistId?: { value: string };
};

type CollectionResp = Pick<
  Collection,
  "id" | "title" | "description" | "descriptionHtml" | "handle"
> & {
  image: Image;
  products: {
    nodes: ProductNode[];
  };
};

type CollectionQueryVars = {
  handle: string;
  numProducts?: number;
};

const getCollection = (vars: CollectionQueryVars) =>
  client.query<{ collection: CollectionResp | null }>(COLLECTION_QUERY, vars);

const useCollectionQuery = (vars: CollectionQueryVars) =>
  useQuery(["collection", "detail", vars], () => getCollection(vars), {
    select: (data) =>
      data.collection
        ? {
            ...data.collection,
            products: flattenConnection(data.collection?.products).map(
              (prod) => ({
                ...prod,
                artistId: prod?.artistId?.value,
              }),
            ),
          }
        : null,
  });

type CollectionQueryData = NonNullable<
  ReturnType<typeof useCollectionQuery>["data"]
>;
type CollectionQueryProductData = CollectionQueryData["products"][number];

export type { CollectionQueryData, CollectionQueryProductData };
export { useCollectionQuery };
